import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Search, Filter, Copy } from "lucide-react";

const STATUS_LABEL = {
  pending: { label: "Aguardando", cls: "border-warning/40 text-warning bg-warning/5" },
  approved: { label: "Aprovado", cls: "border-success/40 text-success bg-success/5" },
  rejected: { label: "Rejeitado", cls: "border-destructive/40 text-destructive bg-destructive/5" },
};

export default function AdminPixPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/pix", { params: statusFilter ? { status: statusFilter } : {} });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao carregar dados."); }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return items;
    return items.filter((tx) => (
      (tx.patient_name || "").toLowerCase().includes(ql)
      || (tx.patient_email || "").toLowerCase().includes(ql)
      || (tx.charge_id || "").toLowerCase().includes(ql)
    ));
  }, [items, q]);

  const approve = async (tx) => {
    if (!window.confirm(`Confirmar recebimento de R$ ${Number(tx.amount).toFixed(2)} de ${tx.patient_name}? O plano ${tx.plan} será ativado imediatamente.`)) return;
    setBusyId(tx.charge_id);
    try {
      await api.post(`/admin/pix/${tx.charge_id}/approve`);
      toast.success("PIX aprovado e plano ativado.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao aprovar.");
    } finally { setBusyId(null); }
  };

  const reject = async (tx) => {
    if (!window.confirm(`Rejeitar esta cobrança? Isso NÃO ativa o plano.`)) return;
    setBusyId(tx.charge_id);
    try {
      await api.post(`/admin/pix/${tx.charge_id}/reject`);
      toast.success("Cobrança rejeitada.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao rejeitar.");
    } finally { setBusyId(null); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code || "");
    toast.success("Código PIX copiado.");
  };

  return (
    <div className="space-y-6" data-testid="admin-pix-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-gold mb-3">Financeiro</div>
          <h1 className="brand-wordmark text-3xl md:text-4xl font-bold tracking-tight uppercase text-primary">PIX</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Confirme o recebimento de cada PIX após verificar no extrato bancário. O plano do paciente é ativado no clique — não desfaça sem certeza.
          </p>
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input placeholder="Buscar por nome, email ou charge_id…" value={q} onChange={(e) => setQ(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-sm border border-input bg-card text-sm"
            data-testid="pix-search-input" />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 rounded-sm border border-input bg-card text-sm"
            data-testid="pix-status-filter">
            <option value="pending">Aguardando</option>
            <option value="approved">Aprovado</option>
            <option value="rejected">Rejeitado</option>
            <option value="">Todos</option>
          </select>
        </div>
        <div className="text-xs text-muted-foreground ml-auto">{filtered.length} cobrança{filtered.length === 1 ? "" : "s"}</div>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-left">
            <tr>
              <th className="px-4 py-3 overline text-muted-foreground">Paciente</th>
              <th className="px-4 py-3 overline text-muted-foreground">Plano</th>
              <th className="px-4 py-3 overline text-muted-foreground">Valor</th>
              <th className="px-4 py-3 overline text-muted-foreground">Charge</th>
              <th className="px-4 py-3 overline text-muted-foreground">Data</th>
              <th className="px-4 py-3 overline text-muted-foreground">Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filtered.map((tx) => {
              const st = STATUS_LABEL[tx.status] || { label: tx.status, cls: "border-border text-muted-foreground bg-muted" };
              return (
                <tr key={tx.charge_id} className="border-b border-border last:border-0 hover:bg-muted/40 transition-colors" data-testid={`pix-row-${tx.charge_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{tx.patient_name}</div>
                    <div className="text-xs text-muted-foreground">{tx.patient_email}</div>
                  </td>
                  <td className="px-4 py-3 capitalize">{tx.plan}</td>
                  <td className="px-4 py-3 font-semibold text-primary">R$ {Number(tx.amount).toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => copyCode(tx.charge_id)} className="font-mono text-xs inline-flex items-center gap-1 hover:text-primary" title="Copiar charge id">
                      {tx.charge_id.slice(0, 12)}… <Copy className="w-3 h-3" />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{tx.created_at ? new Date(tx.created_at).toLocaleString("pt-BR") : ""}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block overline px-2 py-0.5 rounded-sm border ${st.cls}`}>{st.label}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {tx.status === "pending" && (
                      <div className="inline-flex gap-2">
                        <button onClick={() => approve(tx)} disabled={busyId === tx.charge_id}
                          className="inline-flex items-center gap-1 rounded-sm bg-success text-white px-3 py-1.5 text-xs font-semibold hover:opacity-90 transition disabled:opacity-50"
                          data-testid={`approve-pix-${tx.charge_id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5" /> Aprovar
                        </button>
                        <button onClick={() => reject(tx)} disabled={busyId === tx.charge_id}
                          className="inline-flex items-center gap-1 rounded-sm border border-destructive/50 text-destructive px-3 py-1.5 text-xs font-semibold hover:bg-destructive/5 transition disabled:opacity-50"
                          data-testid={`reject-pix-${tx.charge_id}`}>
                          <XCircle className="w-3.5 h-3.5" /> Rejeitar
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">Nenhuma cobrança encontrada.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
